import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkle, PaperPlaneRight, ArrowLeft, CheckCircle, ArrowCounterClockwise, Camera } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const newSessionId = () => `aip-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const GREETING = "Hi! I'm your project assistant. Tell me what you need done — for example, \"I need my bathroom remodeled\" — and I'll put together a project that contractors can quote.";

const fmtBudget = (min, max) => {
  const f = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : null);
  if (f(min) && f(max)) return `${f(min)} – ${f(max)}`;
  return f(max) || f(min) || 'Flexible';
};

export default function AIProjectChat() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('ai_project_session') || newSessionId());
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [posted, setPosted] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem('token')) { navigate('/login?redirect=/ai-project'); return; }
    localStorage.setItem('ai_project_session', sessionId);
    axios.get(`${API}/api/ai-project/session/${sessionId}`, { headers: authHeaders() })
      .then((r) => {
        if (r.data.messages?.length) {
          setMessages([{ role: 'assistant', content: GREETING }, ...r.data.messages]);
          setPhotos(r.data.photos || []);
          if (r.data.status === 'posted') setPosted({ job_id: r.data.job_id });
          else if (r.data.project) setDraft(r.data.project);
        }
      })
      .catch(() => {});
  }, [sessionId, navigate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, draft, posted, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ai-project/chat`, { session_id: sessionId, message: text }, { headers: authHeaders() });
      setMessages((m) => [...m, { role: 'assistant', content: r.data.reply }]);
      if (r.data.done && r.data.project) setDraft(r.data.project);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: e.response?.data?.detail || 'Something went wrong — please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;
    if (file.size > 8 * 1024 * 1024) {
      setMessages((m) => [...m, { role: 'assistant', content: 'That photo is over 8 MB — please pick a smaller one.' }]);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/api/ai-project/upload-photo?session_id=${sessionId}`, form, { headers: authHeaders() });
      setPhotos((p) => [...p, r.data.url]);
      setMessages((m) => [...m, { role: 'user', content: '[Attached a photo of the project area]', photo: r.data.url }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: err.response?.data?.detail || 'Photo upload failed — please try again.' }]);
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/ai-project/confirm`, { session_id: sessionId }, { headers: authHeaders() });
      setPosted(r.data);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: e.response?.data?.detail || 'Could not post the project — please try again.' }]);
    } finally {
      setBusy(false);
    }
  };

  const startOver = () => {
    const s = newSessionId();
    localStorage.setItem('ai_project_session', s);
    setSessionId(s);
    setMessages([{ role: 'assistant', content: GREETING }]);
    setDraft(null);
    setPosted(null);
    setPhotos([]);
  };

  return (
    <div className="h-[calc(100dvh-64px)] bg-[#0F172A] flex flex-col" data-testid="ai-project-chat-page">
      {/* Header */}
      <div className="border-b-2 border-[#D95800] bg-[#0F172A] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-neutral-400 hover:text-white" data-testid="ai-project-back-btn">
            <ArrowLeft size={20} weight="bold" />
          </button>
          <Sparkle size={20} weight="fill" className="text-[#D95800]" />
          <div className="flex-1">
            <p className="text-white font-black uppercase text-sm leading-none">AI Project Assistant</p>
            <p className="text-neutral-500 text-[11px] mt-0.5">Describe it — I'll build the project</p>
          </div>
          <button onClick={startOver} className="text-neutral-400 hover:text-white flex items-center gap-1 text-[11px] font-bold uppercase" data-testid="ai-project-restart-btn">
            <ArrowCounterClockwise size={14} weight="bold" /> Restart
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto min-h-0" ref={scrollRef}>
        <div className="max-w-2xl mx-auto px-4 py-5 space-y-3" data-testid="ai-project-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed border-2 border-black rounded-2xl ${
                  m.role === 'user'
                    ? 'bg-[#D95800] text-white rounded-br-sm'
                    : 'bg-white text-neutral-900 rounded-bl-sm'
                }`}
                data-testid={`ai-msg-${m.role}-${i}`}
              >
                {m.photo ? (
                  <img src={`${API}${m.photo}`} alt="Project" className="rounded-lg max-h-48 border border-black" data-testid={`ai-msg-photo-${i}`} />
                ) : (
                  m.content
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white border-2 border-black rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5" data-testid="ai-typing-indicator">
                {[0, 1, 2].map((d) => (
                  <span key={d} className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                ))}
              </div>
            </div>
          )}

          {/* Draft card */}
          {draft && !posted && (
            <div className="bg-white border-2 border-black rounded-2xl p-5 shadow-[5px_5px_0px_#D95800]" data-testid="ai-project-draft-card">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#D95800] mb-1">Your Project Draft</p>
              <h2 className="font-black text-lg text-neutral-900 leading-snug">{draft.title}</h2>
              <p className="text-sm text-neutral-600 mt-2">{draft.description}</p>
              {draft.scope_items?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {draft.scope_items.map((s, i) => (
                    <li key={i} className="text-sm text-neutral-700 flex gap-2"><span className="text-[#D95800] font-black">•</span>{s}</li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="bg-neutral-100 border border-black rounded-lg py-2">
                  <p className="text-[9px] font-black uppercase text-neutral-400">Budget</p>
                  <p className="text-xs font-black text-neutral-900">{fmtBudget(draft.budget_min, draft.budget_max)}</p>
                </div>
                <div className="bg-neutral-100 border border-black rounded-lg py-2">
                  <p className="text-[9px] font-black uppercase text-neutral-400">Timeline</p>
                  <p className="text-xs font-black text-neutral-900 capitalize">{(draft.timeline || 'flexible').replace('_', ' ')}</p>
                </div>
                <div className="bg-neutral-100 border border-black rounded-lg py-2">
                  <p className="text-[9px] font-black uppercase text-neutral-400">Location</p>
                  <p className="text-xs font-black text-neutral-900">{[draft.city, draft.state].filter(Boolean).join(', ') || '—'}</p>
                </div>
              </div>
              {photos.length > 0 && (
                <div className="flex items-center gap-2 mt-3 overflow-x-auto" data-testid="ai-draft-photos">
                  {photos.map((p, i) => (
                    <img key={i} src={`${API}${p}`} alt="" className="w-14 h-14 object-cover rounded-lg border border-black shrink-0" />
                  ))}
                  <span className="text-[11px] font-bold text-neutral-500 shrink-0">{photos.length} photo{photos.length === 1 ? '' : 's'} attached</span>
                </div>
              )}
              <button
                onClick={confirm}
                disabled={busy}
                className="w-full mt-4 bg-emerald-600 text-white font-black uppercase text-sm py-3 border-2 border-black rounded-xl disabled:opacity-50"
                data-testid="ai-project-post-btn"
              >
                🚀 Post This Project
              </button>
              <p className="text-[11px] text-neutral-400 text-center mt-2">Want changes? Just keep chatting below.</p>
            </div>
          )}

          {/* Posted */}
          {posted && (
            <div className="bg-white border-2 border-black rounded-2xl p-5 text-center shadow-[5px_5px_0px_#059669]" data-testid="ai-project-posted-card">
              <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto mb-2" />
              <h2 className="font-black uppercase text-lg text-neutral-900">Project Posted! 🎉</h2>
              <p className="text-sm text-neutral-600 mt-1">
                {posted.matched_contractors
                  ? `${posted.matched_contractors} matching contractor${posted.matched_contractors === 1 ? '' : 's'} are being notified. Quotes will land in your dashboard.`
                  : 'Contractors are being notified — quotes will land in your dashboard.'}
              </p>
              <div className="flex gap-2 mt-4">
                <Link to="/homeowner-dashboard" className="flex-1 bg-[#D95800] text-white font-black uppercase text-xs py-3 border-2 border-black rounded-xl" data-testid="ai-project-dashboard-link">
                  View My Projects
                </Link>
                <button onClick={startOver} className="flex-1 bg-white text-neutral-900 font-black uppercase text-xs py-3 border-2 border-black rounded-xl" data-testid="ai-project-new-btn">
                  Start Another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      {!posted && (
        <div className="border-t-2 border-black bg-[#0F172A] sticky bottom-0">
          <div className="max-w-2xl mx-auto px-4 pt-3 pb-20 md:pb-3 flex gap-2">
            <input type="file" accept="image/*" ref={fileRef} onChange={uploadPhoto} className="hidden" data-testid="ai-project-file-input" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bg-white text-neutral-700 border-2 border-black rounded-xl px-3 disabled:opacity-50"
              data-testid="ai-project-photo-btn"
              aria-label="Attach photo"
            >
              {uploading
                ? <span className="block w-5 h-5 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
                : <Camera size={20} weight="bold" />}
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={draft ? 'Want to tweak something? Tell me...' : 'e.g. I need my bathroom remodeled'}
              className="flex-1 bg-white border-2 border-black rounded-xl px-4 py-3 text-sm font-semibold text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#D95800]"
              data-testid="ai-project-input"
              disabled={busy}
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-[#D95800] text-white border-2 border-black rounded-xl px-4 disabled:opacity-50"
              data-testid="ai-project-send-btn"
              aria-label="Send"
            >
              <PaperPlaneRight size={20} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
